import { supabase } from '../lib/supabase';

/**
 * Organization Invitation Service
 * Handles invitation operations for organizations
 */

export const invitationService = {
  /**
   * Send invitation to join organization
   */
  async sendInvitation(invitationData) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get inviter's role information
      const { data: inviterRoles, error: rolesError } = await supabase
        ?.from('user_role_assignments')?.select(`role_id,system_roles(hierarchy_level)`)?.eq('user_id', user?.id);

      if (rolesError) throw rolesError;

      // Get the highest hierarchy level of the inviter
      const inviterHierarchyLevel = Math.max(
        ...inviterRoles?.map(ra => ra?.system_roles?.hierarchy_level || 0)
      );

      // Get the role being invited to
      const { data: invitedRole, error: roleError } = await supabase
        ?.from('system_roles')?.select('hierarchy_level, display_name')?.eq('id', invitationData?.roleId)
        ?.single();

      if (roleError) throw roleError;

      // Validation: Check if inviter can invite to this role
      if (inviterHierarchyLevel < 100 && invitedRole?.hierarchy_level >= inviterHierarchyLevel) {
        throw new Error(
          `You cannot invite users to the role "${invitedRole?.display_name}" as it is at or above your hierarchy level. ` +
          `You can only invite users to roles below your current level.`
        );
      }

      const { data, error } = await supabase?.from('organization_invitations')?.insert({
          tenant_id: invitationData?.tenantId,
          email: invitationData?.email,
          role_id: invitationData?.roleId,
          invited_by: user?.id,
          message: invitationData?.message || null,
          metadata: invitationData?.metadata || {}
        })?.select(`
          *,
          tenant:tenants(name, subdomain),
          role:system_roles(display_name, hierarchy_level),
          inviter:user_profiles!invited_by(first_name, last_name, email)
        `)?.single();

      if (error) throw error;

      return {
        id: data?.id,
        tenantId: data?.tenant_id,
        email: data?.email,
        roleId: data?.role_id,
        status: data?.status,
        invitationToken: data?.invitation_token,
        expiresAt: data?.expires_at,
        message: data?.message,
        tenant: data?.tenant,
        role: data?.role,
        inviter: data?.inviter,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Send invitation error:', error);
      throw error;
    }
  },

  /**
   * Get all invitations for current user's tenant
   */
  async getInvitationsForTenant(tenantId) {
    try {
      const { data, error } = await supabase?.from('organization_invitations')?.select(`
          *,
          role:system_roles(display_name, hierarchy_level),
          inviter:user_profiles!invited_by(first_name, last_name, email)
        `)?.eq('tenant_id', tenantId)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(inv => ({
        id: inv?.id,
        tenantId: inv?.tenant_id,
        email: inv?.email,
        roleId: inv?.role_id,
        status: inv?.status,
        invitationToken: inv?.invitation_token,
        expiresAt: inv?.expires_at,
        acceptedAt: inv?.accepted_at,
        declinedAt: inv?.declined_at,
        message: inv?.message,
        role: inv?.role,
        inviter: inv?.inviter,
        createdAt: inv?.created_at,
        updatedAt: inv?.updated_at
      }));
    } catch (error) {
      console.error('Get tenant invitations error:', error);
      throw error;
    }
  },

  /**
   * Get invitations sent to current user's email
   */
  async getMyInvitations() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase?.from('user_profiles')?.select('email')?.eq('id', user?.id)?.single();

      if (!profile) throw new Error('Profile not found');

      const { data, error } = await supabase?.from('organization_invitations')?.select(`
          *,
          tenant:tenants(name, subdomain, logo_url),
          role:system_roles(display_name, hierarchy_level),
          inviter:user_profiles!invited_by(first_name, last_name, email)
        `)?.eq('email', profile?.email)?.eq('status', 'pending')?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(inv => ({
        id: inv?.id,
        tenantId: inv?.tenant_id,
        email: inv?.email,
        roleId: inv?.role_id,
        status: inv?.status,
        invitationToken: inv?.invitation_token,
        expiresAt: inv?.expires_at,
        message: inv?.message,
        tenant: inv?.tenant,
        role: inv?.role,
        inviter: inv?.inviter,
        createdAt: inv?.created_at
      }));
    } catch (error) {
      console.error('Get my invitations error:', error);
      throw error;
    }
  },

  /**
   * Accept invitation
   */
  async acceptInvitation(invitationId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get invitation details
      const { data: invitation, error: invError } = await supabase?.from('organization_invitations')?.select('*')?.eq('id', invitationId)?.single();

      if (invError) throw invError;
      if (!invitation) throw new Error('Invitation not found');

      // Update invitation status
      const { error: updateError } = await supabase?.from('organization_invitations')?.update({
          status: 'accepted',
          accepted_at: new Date()?.toISOString()
        })?.eq('id', invitationId);

      if (updateError) throw updateError;

      // Assign role to user
      const { error: roleError } = await supabase?.from('user_role_assignments')?.insert({
          user_id: user?.id,
          role_id: invitation?.role_id,
          tenant_id: invitation?.tenant_id,
          assigned_by: invitation?.invited_by
        });

      if (roleError) throw roleError;

      // Update user profile tenant_id if not set
      const { data: profile } = await supabase?.from('user_profiles')?.select('tenant_id')?.eq('id', user?.id)?.single();

      if (!profile?.tenant_id) {
        await supabase?.from('user_profiles')?.update({ tenant_id: invitation?.tenant_id })?.eq('id', user?.id);
      }

      return { success: true };
    } catch (error) {
      console.error('Accept invitation error:', error);
      throw error;
    }
  },

  /**
   * Decline invitation
   */
  async declineInvitation(invitationId) {
    try {
      const { error } = await supabase?.from('organization_invitations')?.update({
          status: 'declined',
          declined_at: new Date()?.toISOString()
        })?.eq('id', invitationId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Decline invitation error:', error);
      throw error;
    }
  },

  /**
   * Cancel invitation (by sender)
   */
  async cancelInvitation(invitationId) {
    try {
      const { error } = await supabase?.from('organization_invitations')?.update({
          status: 'cancelled'
        })?.eq('id', invitationId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Cancel invitation error:', error);
      throw error;
    }
  },

  /**
   * Resend invitation
   */
  async resendInvitation(invitationId) {
    try {
      const { error } = await supabase?.from('organization_invitations')?.update({
          expires_at: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000)?.toISOString(),
          invitation_token: crypto.randomUUID()
        })?.eq('id', invitationId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Resend invitation error:', error);
      throw error;
    }
  },

  /**
   * Delete invitation
   */
  async deleteInvitation(invitationId) {
    try {
      const { error } = await supabase?.from('organization_invitations')?.delete()?.eq('id', invitationId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Delete invitation error:', error);
      throw error;
    }
  }
};